import { useParams } from 'react-router';
import { PageHeaderWithBackButton } from '../../../components/common';
import {
  useFetchEnums,
  useFetchMainCategories,
} from '../../../services/FavePass';
import { Row, Spin } from 'antd';
import { Form, SubmitButton } from '../../../components/common/form';
import { useMerchantConnectFavePassFormBuilder } from '../../FavePassesView/helpers/useMerchantConnectFavePassFormBuilder';
import { useFetchCompanyDetails } from '../../../services/Company/useFetchCompanyDetails';
import { useFetchCompanyBankAccounts } from '../../../services/Company/useFetchCompanyBankAccounts';
import FormFields from '../../../components/FormFields';
import { useCreateFavePassViaMerchantConnect } from '../../../services/FavePasses/useCreateFavePassViaMerchantConnect';
import { FormikHelpers } from 'formik';
import { Message } from 'fave-ui';
import { toolRedirect } from '../../../services/http';
import useSetBreadCrumbParams from '../../../hooks/useSetBreadCrumbParams';
import { useFetchCompanyOutlets } from '../../../services/Company/useFetchCompanyOutlets';
import { useUserContext } from '../../../contexts/UserContext';
import { useFetchFlipper } from '../../../services/useFetchFlipper';
import LockPage from '../../../components/LockPage';
import { useState } from 'react';
import { useFetchFavePassDetailsViaMerchantConnect } from '../../../services/FavePasses/useFetchFavePassViaMerchantConnect';
import { useUpdateFavePassViaMerchantConnect } from '../../../services/FavePasses/useUpdateFavePassViaMerchantConnect';
import { UploadFile } from 'antd/es/upload/interface';
import { fetchImageAsFile, getFileFormat } from '../../../helpers/formHelpers';
import { convertToDays, formatDate } from '../../../utils/utilFunctions';

const MerchantConnectFavePassForm = () => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { mode, id, fave_pass_id } = useParams();
  const companyId = String(id);
  const favePassId = String(fave_pass_id);
  const isEditMode = mode === 'edit';

  const { getPageSettings } = useUserContext();
  const partnerSettings = getPageSettings('merchant_connect');
  const {
    data: disableFavePassOnboardingFlipper,
    isFetching: isFetchingFavePassOnboardingFlipper,
  } = useFetchFlipper({ key: 'disable_fave_pass_onboarding' });
  const canShowFavePass =
    !!partnerSettings?.show && !!!disableFavePassOnboardingFlipper?.value;
  // update has same permissions as show fave pass
  const canUpdateFavePass = canShowFavePass;
  // disable forms based on permission
  const disableFormFields = !canUpdateFavePass;
  const [disabledSubmitButton, setDisabledSubmitButton] = useState(false);

  // fetch company details
  const { data: companyDetails, isFetching: isFetchingCompanyDetails } =
    useFetchCompanyDetails({
      company_id: companyId,
    });

  useSetBreadCrumbParams(
    [{ label: String(companyDetails?.name), key: companyId }],
    !!companyDetails,
  );

  const pageTitle = `${isEditMode ? 'Edit' : 'Create'} Fave Pass`;
  const subTitle = !isFetchingCompanyDetails && (
    <span>
      Add Fave Pass for <strong>{companyDetails?.name}</strong>
    </span>
  );

  // fetch enums for status and reservation required type select items
  const { data: favePassEnums, isFetching: isFetchingEnums } = useFetchEnums();
  // fetch main categories for main category select items
  const { data: favePassMainCategories, isFetching: isFetchingMainCategories } =
    useFetchMainCategories();
  // fetch company bank accounts
  const {
    data: companyBankAccounts,
    isFetching: isFetchingCompanyBankAccounts,
  } = useFetchCompanyBankAccounts({
    company_id: companyId,
  });
  // fetch company outlets
  const { data: companyOutlets, isFetching: isFetchingCompanyOutlets } =
    useFetchCompanyOutlets({
      company_id: companyId,
    });
  const { data: favePassDetails, isFetching: isFetchingFavePassDetails } =
    useFetchFavePassDetailsViaMerchantConnect(
      {
        fave_passes_id: favePassId,
      },
      // run only when in edit mode
      isEditMode,
    );
  // fetch form fields, form schema and initial values
  const { formFields, formSchema, initialValues } =
    useMerchantConnectFavePassFormBuilder({
      favePassEnums,
      favePassMainCategories,
      companyBankAccounts,
      companyOutlets,
      favePassDetails,
    });

  const { mutate: createFavePass, isLoading: isLoadingCreateFavePass } =
    useCreateFavePassViaMerchantConnect();
  const { mutate: updateFavePass, isLoading: isLoadingUpdateFavePass } =
    useUpdateFavePassViaMerchantConnect();

  // wait until all necessary data is fetched
  const isFormLoading =
    isFetchingCompanyDetails ||
    isFetchingEnums ||
    isFetchingMainCategories ||
    isFetchingCompanyBankAccounts ||
    isFetchingCompanyOutlets ||
    isFetchingFavePassOnboardingFlipper ||
    isLoadingCreateFavePass ||
    isLoadingUpdateFavePass ||
    isFetchingFavePassDetails;

  const handleFormSubmit = async (
    {
      image_url,
      start_date,
      end_date,
      validity,
      validity_type,
      ...restValues
    }: typeof initialValues,
    { setSubmitting }: FormikHelpers<typeof initialValues>,
  ) => {
    const formData = new FormData();
    let imageObject: File | undefined;
    const imageUrl = Array.isArray(image_url)
      ? (image_url[0] as UploadFile)
      : undefined;

    // image data populated from api response
    if (imageUrl && imageUrl.url) {
      const fileFormat = getFileFormat(imageUrl.url);
      const fileNameArray = imageUrl.url?.split('/');
      const fileName = fileNameArray
        ? fileNameArray[fileNameArray.length - 1]
        : `image.${fileFormat}`;

      imageObject = await fetchImageAsFile(imageUrl.url, fileName);
    }
    // new uploaded image
    else if (imageUrl && imageUrl.originFileObj) {
      imageObject = imageUrl.originFileObj;
    }

    // calculate validity in days based on selected validity type
    const validityInDays = convertToDays(validity, validity_type);

    const payload = {
      ...restValues,
      validity: validityInDays,
      start_date: formatDate(start_date, 'yyyy-MM-dd'),
      end_date: formatDate(end_date, 'yyyy-MM-dd'),
      ...(imageObject && { image: imageObject }),
    };

    const handleSuccess = (message: string) => {
      setDisabledSubmitButton(true);
      Message.success({ content: message }).then(() =>
        toolRedirect(
          `/admin/merchant_connect/merchants/${companyId}/fave_passes`,
        ),
      );
    };

    // appends data to mc_product[product_details] in formData
    const appendMcProductProductDetailsFormData = (
      key: string,
      value: File | string | number | (string | number)[],
    ) => {
      if (value instanceof File) {
        // Append files directly
        formData.append(`mc_product[product_details][${key}]`, value);
      } else if (Array.isArray(value)) {
        // Append selected outlet ids as array
        value.forEach(outletId =>
          formData.append(
            `mc_product[product_details][${key}][]`,
            String(outletId),
          ),
        );
      } else {
        // Append other values as strings
        formData.append(
          `mc_product[product_details][${key}]`,
          value != null ? String(value) : '',
        );
      }
    };

    for (const key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) {
        const value = payload[key as keyof typeof payload] as unknown as string;
        appendMcProductProductDetailsFormData(key, value);
      }
    }

    if (isEditMode) {
      updateFavePass(
        { mc_product: formData, fave_pass_id: favePassId },
        {
          onSuccess: () => handleSuccess('Successfully updated Fave Pass'),
          onSettled: () => setSubmitting(false),
        },
      );
    } else {
      formData.append('mc_product[company_id]', companyId);
      formData.append('mc_product[product_type]', 'FavePass');

      createFavePass(
        { mc_product: formData },
        {
          onSuccess: () => handleSuccess('Successfully created Fave Pass'),
          onSettled: () => setSubmitting(false),
        },
      );
    }
  };

  return (
    <>
      {canShowFavePass ? (
        <>
          <PageHeaderWithBackButton title={pageTitle} subTitle={subTitle} />
          <Spin spinning={isFormLoading}>
            <Form
              initialValues={initialValues}
              onSubmit={handleFormSubmit}
              validationSchema={formSchema}
              validateOnMount
              validateOnChange
              validateOnSchemaChange
            >
              <div className="flex justify-center">
                <div className="max-w-[750px]">
                  <Row gutter={24}>
                    <FormFields
                      formFields={formFields}
                      disableFormFields={disableFormFields}
                    />
                  </Row>
                  <div className={'flex justify-end'}>
                    <SubmitButton
                      disableIfNotValid
                      isDisabled={disableFormFields || disabledSubmitButton}
                      loading={
                        isLoadingCreateFavePass || isLoadingUpdateFavePass
                      }
                    >
                      {(_, isSubmitting) =>
                        isSubmitting ? 'Saving...' : 'Submit'
                      }
                    </SubmitButton>
                  </div>
                </div>
              </div>
            </Form>
          </Spin>
        </>
      ) : (
        <LockPage />
      )}
    </>
  );
};

export default MerchantConnectFavePassForm;
